// Anant Group Buy Landing — public share link page (/gb/:gid)
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';

const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL || ''}${u}`);

export default function GroupBuyLanding() {
  const { gid } = useParams();
  const [gb, setGb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState(1);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/group-buy/${gid}`);
      setGb(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Not found');
    } finally { setLoading(false); }
  }, [gid]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const join = async () => {
    if (!localStorage.getItem('token')) { window.location.href = '/login'; return; }
    setBusy(true);
    try {
      const r = await api.post(`/group-buy/join/${gid}`, { quantity: qty });
      setGb((prev) => ({ ...(prev || {}), ...(r.data.group_buy || {}) }));
      toast.success('✅ Joined! Timer expire ya quorum me auto-confirm.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Join failed'); }
    finally { setBusy(false); }
  };

  const leave = async () => {
    if (!window.confirm('Group Buy chhodna hai? Aapka AT refund ho jayega.')) return;
    setBusy(true);
    try {
      await api.post(`/group-buy/leave/${gid}`);
      toast.success('👋 Left · AT refunded');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Leave failed'); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('📋 Link copied'); }
    catch { toast.error('Copy failed'); }
  };
  const waShare = () => {
    const t = gb?.listing_title || 'this product';
    const msg = `🎁 Group Buy on *${t}* — Min ${gb?.min_buyers} buyers · ${gb?.discount_pct}% OFF · 24h only\n\nJoin: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Loading…</div>;
  if (!gb) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Group Buy not found</div>;

  const secondsLeft = Math.max(0, (gb.seconds_left || 0) - Math.floor((now - Date.now()) / 1000));
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  const image = gb.listing_image || (gb.listing_snapshot?.images || [])[0];
  const isActive = gb.status === 'active';
  const isConfirmed = gb.status === 'confirmed';
  const isExpired = gb.status === 'expired';
  const myUserId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; } })();
  const alreadyJoined = myUserId && (gb.participants || []).some((p) => p.user_id === myUserId);
  const myRole = (gb.participants || []).find((p) => p.user_id === myUserId)?.role;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 text-white pb-24" data-testid="gb-landing">
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <a href="/marketplace" className="text-xs text-fuchsia-300 font-black">← Marketplace</a>

        <div className="rounded-2xl bg-slate-900/80 border-2 border-fuchsia-400/40 overflow-hidden">
          {image ? <img src={abs(image)} alt="" className="w-full h-56 object-cover" />
                 : <div className="w-full h-56 bg-slate-800 flex items-center justify-center text-6xl">🎁</div>}
          <div className="p-4 space-y-3">
            <div className="text-lg font-black text-white" data-testid="gb-title">{gb.listing_title}</div>
            <div className="text-[11px] opacity-75">{gb.shop_name}{gb.listing_snapshot?.city ? ` · ${gb.listing_snapshot.city}` : ''}</div>

            <div className="flex items-center gap-2">
              <div className="text-2xl font-black text-emerald-300">{Number(gb.discounted_price).toFixed(0)} AT</div>
              <div className="text-sm line-through opacity-60">{Number(gb.original_price).toFixed(0)} AT</div>
              <span className="ml-auto px-2 py-1 rounded bg-fuchsia-500 text-white text-xs font-black">−{Number(gb.discount_pct).toFixed(0)}%</span>
            </div>

            {/* Status banner */}
            {isConfirmed && (
              <div className="rounded-xl bg-emerald-500/20 border-2 border-emerald-400 p-3 text-center" data-testid="gb-status-confirmed">
                <div className="text-lg font-black text-emerald-300">✅ Group Buy Confirmed!</div>
                <div className="text-[11px] opacity-90">{(gb.order_ids || []).length} orders placed · Sellers ko notify kar diya.</div>
              </div>
            )}
            {isExpired && (
              <div className="rounded-xl bg-rose-500/20 border-2 border-rose-400 p-3 text-center" data-testid="gb-status-expired">
                <div className="text-lg font-black text-rose-300">⌛ Expired</div>
                <div className="text-[11px] opacity-90">Minimum buyers nahi mile · Sabko refund ho gaya.</div>
              </div>
            )}
            {isActive && (
              <>
                {/* Countdown */}
                <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-3">
                  <div className="text-[10px] uppercase opacity-70 font-black">Time Remaining</div>
                  <div className="text-3xl font-black text-fuchsia-300 tabular-nums" data-testid="gb-countdown">
                    {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
                  </div>
                </div>
                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between text-[11px] font-black mb-1">
                    <span>{gb.joined_count} / {gb.min_buyers} joined</span>
                    <span className="text-fuchsia-300">{gb.progress_pct}%</span>
                  </div>
                  <div className="h-3 bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-all"
                         style={{ width: `${gb.progress_pct}%` }} data-testid="gb-progress" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {isActive && !alreadyJoined && (
          <div className="rounded-2xl bg-slate-900/80 border-2 border-fuchsia-400/40 p-4 space-y-3">
            {gb.max_qty_per_buyer > 1 && (
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-black text-fuchsia-200 uppercase">Quantity</span>
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-lg bg-slate-800 text-white font-black text-lg" data-testid="gb-qty-minus">−</button>
                <span className="text-xl font-black text-white w-8 text-center" data-testid="gb-qty">{qty}</span>
                <button onClick={() => setQty(Math.min(gb.max_qty_per_buyer, qty + 1))} className="w-9 h-9 rounded-lg bg-slate-800 text-white font-black text-lg" data-testid="gb-qty-plus">+</button>
                <span className="text-[10px] opacity-70 ml-auto">max {gb.max_qty_per_buyer}</span>
              </div>
            )}
            <button onClick={join} disabled={busy} data-testid="gb-join-btn"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-black text-lg disabled:opacity-50">
              {busy ? '...' : `🎁 Join Group Buy · ${(Number(gb.discounted_price) * qty).toFixed(0)} AT`}
            </button>
            <div className="text-[10px] opacity-75 text-center">AT ab hi debit hoga. Timer expire ho gaya to auto-refund.</div>
          </div>
        )}

        {isActive && alreadyJoined && (
          <div className="rounded-2xl bg-emerald-500/10 border-2 border-emerald-400/50 p-4 space-y-2 text-center">
            <div className="text-emerald-300 font-black" data-testid="gb-already-joined">✅ You already joined</div>
            <div className="text-[10px] opacity-80">Timer expire ya quorum reach me auto-confirm hoga.</div>
            {myRole !== 'initiator' && (
              <button onClick={leave} disabled={busy} data-testid="gb-leave-btn"
                      className="w-full py-2 rounded-xl bg-rose-600 text-white text-xs font-black disabled:opacity-50">
                Leave & Refund
              </button>
            )}
          </div>
        )}

        <div className="rounded-2xl bg-slate-900/80 border border-slate-700 p-4 space-y-2">
          <div className="text-[11px] font-black text-fuchsia-300 uppercase">Share to invite buyers</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={copy} data-testid="gb-copy" className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black">📋 Copy Link</button>
            <button onClick={waShare} data-testid="gb-wa" className="py-2 rounded-lg bg-green-600 text-white text-xs font-black">📱 WhatsApp</button>
          </div>
        </div>

        {(gb.participants || []).length > 0 && (
          <div className="rounded-2xl bg-slate-900/80 border border-slate-700 p-4 space-y-2">
            <div className="text-[11px] font-black text-fuchsia-300 uppercase">Participants ({(gb.participants || []).length})</div>
            <div className="space-y-1">
              {(gb.participants || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-800 last:border-0">
                  <span className="opacity-90">{p.username || 'user'}{p.role === 'initiator' ? ' 👑' : ''}</span>
                  <span className="text-fuchsia-300 font-black">×{p.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
